import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    TextField,
    Grid,
    Box,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { FieldArray } from "formik";
import { Loadable } from "../../../../common";

const useStyles = makeStyles((theme) => ({
    tabs: {
        borderBottom: '1px solid #ebedf2' 
    },
    closeButton: {
        color: theme.palette.grey[500],
    },
}));

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}

export default function EAPGSettingsDialog({
    open,
    onClose,
    index,
    values,
    setFieldValue,
    getError,
}) {
    const [tabIndex, setTabIndex] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const configPath = `contractEapgconfigurationXref.${index}`;
    const classes = useStyles();
    const dataTypes = ['string', 'number', 'boolean'];
    const grouperTypes = [
        "SEE_SCHEDULE",
        "AHRQ",
        "AMPPC",
        "AP",
        "APC",
        "APR",
        "ASC",
        "CMS",
        "CMSHCC",
        "CMS_IPF",
        "CMS_IRF",
        "CRG",
        "EAPGS",
        "ELIXHAUSER",
        "HHSHCC",
        "HOME_HEALTH_GRPR",
        "IL",
        "IR",
        "MD_APR",
        "MRDFE",
        "NY_APR",
        "OH",
        "PCE",
        "PFE",
        "PFP",
        "PPR",
        "QM",
        "SNF",
        "TRICARE",
        "TRICARE_APC"
    ];
    const reimbursementTypes = [
        "SEE_SCHEDULE",
        "AK_MEDICAID_APR",
        "AL_BCBS_EAPGS",
        "AL_MEDICAID_APR",
        "AZ_MEDICAID_APR",
        "CA_MEDICAID_APR",
        "COST_EAPGS",
        "CO_MEDICAID_APR",
        "CO_MEDICAID_EAPGS",
        "CT_MEDICAID_APC",
        "CT_MEDICAID_APR",
        "CWT_APR",
        "FL_MEDICAID_APR",
        "FL_MEDICAID_EAPGS",
        "GA_MEDICAID_APR",
        "GENERIC_APC",
        "HI_MEDICAID_APR",
        "HOME_HEALTH_RMB",
        "ID_MEDICAID_APR",
        "IL_MEDICAID",
        "IL_MEDICAID_APR",
        "IL_MEDICAID_EAPGS",
        "IN_MEDICAID_APR",
        "IPCT",
        "LA_MEDICAID_APR",
        "MA_BCBS_APR",
        "MA_MCD_APR",
        "MA_MEDICAID_APR",
        "MA_MEDICAID_EAPGS",
        "MD_APR",
        "MD_APR_CASE_MIX",
        "MD_EAPGS",
        "MEDICARE_ASC",
        "MEDICARE_FQHC",
        "MEDICARE_HOSPICE",
        "MEDICARE_IPF",
        "MEDICARE_IPPS",
        "MEDICARE_IRF",
        "MEDICARE_LTCH",
        "MEDICARE_OPPS",
        "MEDICARE_RDF",
        "MEDICARE_SNF_PART_A",
        "MEDICARE_SNF_PART_B",
        "MI_MEDICAID_APR",
        "MN_BCBS_APR",
        "MN_BCBS_EAPGS",
        "MN_MEDICAID_APR",
        "MO_MEDICAID_APR",
        "MS_MEDICAID_APR",
        "MT_MEDICAID_APR",
        "ND_BCBS_APR",
        "ND_BCBS_EAPGS",
        "ND_BCBS_MMC_EAPGS",
        "ND_MEDICAID_APR",
        "ND_MEDICAID_EAPGS",
        "NE_BCBS_APR",
        "NE_BCBS_EAPGS",
        "NE_MEDICAID_APR",
        "NE_MEDICAID_EAPGS",
        "NJ_AP",
        "NJ_MEDICAID_APR",
        "NY_AP",
        "NY_APR",
        "NY_MEDICAID_EAPGS",
        "OH_MEDICAID_APR",
        "OH_MEDICAID_EAPGS",
        "OK_BCBS_EAPGS",
        "OPCT",
        "PA_MEDICAID_APR",
        "PCR",
        "RI_MEDICAID_APR",
        "RWT_IR",
        "SC_MEDICAID_APR",
        "TN_BCBS_APR",
        "TRICARE_IPPS",
        "TRICARE_OPPS",
        "TX_MEDICAID_APR",
        "VA_MEDICAID_APR",
        "VA_MEDICAID_EAPGS",
        "VA_MEDICAID_EAPGS_HOSPITAL",
        "WA_DC_MEDICAID_APR",
        "WA_DC_MEDICAID_EAPGS",
        "WA_HCA_AP",
        "WA_MEDICAID_AP",
        "WA_MEDICAID_APR",
        "WA_MEDICAID_EAPGS",
        "WA_WC_AP",
        "WA_WC_APR",
        "WELLMARK_BCBS_APR",
        "WELLMARK_BCBS_EAPGS",
        "WI_MEDICAID_APR",
        "WI_MEDICAID_EAPGS",
        "WY_MEDICAID_APR"
    ];

    const handleChange = (field, val) => {
        setFieldValue(`${configPath}.${field}`, val);
    };

    const handleSave = () => {
        setLoading(true);
        const current = values.contractEapgconfigurationXref[index];
    
        const common = {
            BEGIN_DATE: current.beginDate,
            END_DATE: current.endDate,
            DESCRIPTION: current.grouperDescription,
            GROUPER_TYPE: current.grouperType,
            REIMBURSEMENT_TYPE: current.reimbursementType
        };
    
        const grouperKey = common.GROUPER_TYPE;
        const grouperSettings = {};
        current.grouperArray?.forEach(item => {
            if (item.name) {
                grouperSettings[item.name] = convertValue(item.value, item.type);
            }
        });
    
        const reimbursementKey = common.REIMBURSEMENT_TYPE;
        const reimbursementSettings = {};
        current.reimbursementArray?.forEach(item => {
            if (item.name) {
                reimbursementSettings[item.name] = convertValue(item.value, item.type);
            }
        });
    
        const scheduleItem = {
            common,
            grouper: {
                [grouperKey]: grouperSettings
            },
            reimbursement: {
                [reimbursementKey]: reimbursementSettings
            }
        };

        setFieldValue(`${configPath}.eapgSettings`, {
            scheduleList: [scheduleItem]
        });

        setLoading(false);
        onClose();
    };
    
    const convertValue = (value, type) => {
        if (value == null) return null;
    
        switch (type) {
            case 'string':
                return value.toString();
            case 'number':
                return Number(value);
            case 'boolean':
                return value === 'true' || value === true;
            case 'date':
                // Expecting value in 'YYYY-MM-DD', convert to 'MMDDYYYY'
                const [year, month, day] = value.split('-');
                return `${month}${day}${year}`;
            default:
                return value;
        }
    };

    const handleImport = () => {
        if(index > 0) {
            const prevConfigValues = values.contractEapgconfigurationXref[index-1];
            if(prevConfigValues?.beginDate) {
                setLoading(true);
                setFieldValue(`${configPath}.beginDate`, prevConfigValues.beginDate);
                setFieldValue(`${configPath}.endDate`, prevConfigValues.endDate);
                setFieldValue(`${configPath}.grouperDescription`, prevConfigValues.grouperDescription);
                setFieldValue(`${configPath}.grouperType`, prevConfigValues.grouperType);
                setFieldValue(`${configPath}.grouperArray`, prevConfigValues.grouperArray);
                setFieldValue(`${configPath}.reimbursementType`, prevConfigValues.reimbursementType);
                setFieldValue(`${configPath}.reimbursementArray`, prevConfigValues.reimbursementArray);
                setLoading(false);
            }
        }
    };
    

    const getFieldValue = (field) => values.contractEapgconfigurationXref[index]?.[field] || (field === "EAPGS" ? "EAPGS" : field === "IL_MEDICAID_EAPGS" ? "IL_MEDICAID_EAPGS": "");

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Loadable loading={loading}>
        <DialogTitle>
            <Grid container alignItems='center'>
                <Grid item xs={10}>
                    EAPG Settings
                </Grid>
                {index > 0 && values.contractEapgconfigurationXref[index-1]?.beginDate && ( 
                    <Grid item xs={2} className='text-right'>
                        <Button variant="contained" color="primary" onClick={handleImport}>Import Settings</Button>
                    </Grid>
                )}
            </Grid>
        </DialogTitle>
        <DialogContent>
            <Tabs className={classes.tabs} value={tabIndex} onChange={(e, newVal) => setTabIndex(newVal)}>
            <Tab label="Common" />
            <Tab label="Grouper" />
            <Tab label="Reimbursement" />
            </Tabs>

            {/* Common Tab */}
            <TabPanel value={tabIndex} index={0}>
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <TextField
                            required
                            label="Begin Date"
                            type="date"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            value={getFieldValue("beginDate")}
                            onChange={(e) => handleChange("beginDate", e.target.value)}
                            helperText={getError('beginDate', index)}
                            error={getError('beginDate', index)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            required
                            label="End Date"
                            type="date"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            value={getFieldValue("endDate")}
                            onChange={(e) => handleChange("endDate", e.target.value)}
                            helperText={getError('endDate', index)}
                            error={getError('endDate', index)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            required
                            label="Grouper Description"
                            fullWidth
                            margin="normal"
                            value={getFieldValue("grouperDescription")}
                            onChange={(e) => handleChange("grouperDescription", e.target.value)}
                            helperText={getError('grouperDescription', index)}
                            error={getError('grouperDescription', index)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl
                            fullWidth
                            margin="normal"
                        >
                            <InputLabel>Grouper Type</InputLabel>
                            <Select
                            value={getFieldValue("grouperType")?.trim() || "EAPGS"}
                            onChange={(e) => handleChange("grouperType", e.target.value)}
                            label="Grouper Type"
                            >
                            {grouperTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                {type}
                                </MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                        </Grid>

                        <Grid item xs={4}>
                        <FormControl
                            fullWidth
                            margin="normal"
                        >
                            <InputLabel>Reimbursement Type</InputLabel>
                            <Select
                            value={getFieldValue("reimbursementType")?.trim() || "IL_MEDICAID_EAPGS"}
                            onChange={(e) => handleChange("reimbursementType", e.target.value)}
                            label="Reimbursement Type"
                            >
                            {reimbursementTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                {type}
                                </MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </TabPanel>

            {/* Grouper Tab */}
            <TabPanel value={tabIndex} index={1}>
            <FieldArray
                name={`${configPath}.grouperArray`}
                render={({ push, remove }) => (
                <>
                    {values.contractEapgconfigurationXref[index]?.grouperArray?.map((item, i) => (
                    <Grid container spacing={2} key={i}>
                        <Grid item xs={5}>
                            <TextField
                                label="Name"
                                fullWidth
                                value={item.name}
                                onChange={(e) =>
                                setFieldValue(`${configPath}.grouperArray.${i}.name`, e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    label="Type"
                                    value={item.type}
                                    onChange={(e) =>
                                        setFieldValue(`${configPath}.grouperArray.${i}.type`, e.target.value)
                                    }
                                >
                                    {dataTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                        {item.type === 'boolean' ? (
                            <FormControl fullWidth>
                                <InputLabel>Value</InputLabel>
                                <Select
                                    label="Value"
                                    value={item.value}
                                    onChange={(e) =>
                                    setFieldValue(`${configPath}.grouperArray.${i}.value`, e.target.value === 'true')
                                    }
                                >
                                    <MenuItem value="true">True</MenuItem>
                                    <MenuItem value="false">False</MenuItem>
                                </Select>
                            </FormControl>
                        ) : item.type === 'date' ? (
                            <TextField
                                label="Value"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={item.value}
                                onChange={(e) =>
                                    setFieldValue(`${configPath}.grouperArray.${i}.value`, e.target.value)
                                }
                            />
                        ) : (
                            <TextField
                                label="Value"
                                type={item.type === 'number' ? 'number' : 'text'}
                                fullWidth
                                value={item.value}
                                onChange={(e) =>
                                    setFieldValue(`${configPath}.grouperArray.${i}.value`, e.target.value)
                                }
                            />
                        )}
                        </Grid>

                        <Grid item xs={2}>
                            {(i >= 0 && values.contractEapgconfigurationXref[index]?.grouperArray.length > 1) && (
                                <IconButton sx={{marginTop:'1rem'}} onClick={() => remove(i)}>
                                    <DeleteIcon />
                                </IconButton>
                            )}
                            {i === 0 && (
                                <IconButton color="primary" sx={{marginTop:'1rem'}} onClick={() => push({ name: "", type: "", value: "" })}>
                                    <AddCircleIcon />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                    ))}
                </>
                )}
            />
            </TabPanel>

            {/* Reimbursement Tab */}
            <TabPanel value={tabIndex} index={2}>
            
            <FieldArray
                name={`${configPath}.reimbursementArray`}
                render={({ push, remove }) => (
                <>
                    {values.contractEapgconfigurationXref[index]?.reimbursementArray?.map((item, i) => (
                    <Grid container spacing={2} key={i}>
                        <Grid item xs={5}>
                            <TextField
                                label="Name"
                                fullWidth
                                value={item.name}
                                onChange={(e) =>
                                setFieldValue(`${configPath}.reimbursementArray.${i}.name`, e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    label="Type"
                                    value={item.type}
                                    onChange={(e) =>
                                        setFieldValue(`${configPath}.reimbursementArray.${i}.type`, e.target.value)
                                    }
                                >
                                    {dataTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                        {item.type === 'boolean' ? (
                            <FormControl fullWidth>
                                <InputLabel>Value</InputLabel>
                                <Select
                                    label="Value"
                                    value={item.value}
                                    onChange={(e) =>
                                    setFieldValue(`${configPath}.reimbursementArray.${i}.value`, e.target.value === 'true')
                                    }
                                >
                                    <MenuItem value="true">True</MenuItem>
                                    <MenuItem value="false">False</MenuItem>
                                </Select>
                            </FormControl>
                        ) : item.type === 'date' ? (
                            <TextField
                                label="Value"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={item.value}
                                onChange={(e) =>
                                    setFieldValue(`${configPath}.reimbursementArray.${i}.value`, e.target.value)
                                }
                            />
                        ) : (
                            <TextField
                                label="Value"
                                type={item.type === 'number' ? 'number' : 'text'}
                                fullWidth
                                value={item.value}
                                onChange={(e) =>
                                    setFieldValue(`${configPath}.reimbursementArray.${i}.value`, e.target.value)
                                }
                            />
                        )}
                        </Grid>
                        <Grid item xs={2}>
                            {(i >= 0 && values.contractEapgconfigurationXref[index]?.reimbursementArray.length > 1) && (
                                <IconButton sx={{marginTop:'1rem'}} onClick={() => remove(i)}>
                                    <DeleteIcon />
                                </IconButton>
                            )}
                            {i === 0 && (
                                <IconButton color="primary" sx={{marginTop:'1rem'}} onClick={() => push({ name: "", type: "", value: "" })}>
                                    <AddCircleIcon />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                    ))}
                </>
                )}
            />
            </TabPanel>
        </DialogContent>
        <DialogActions>
            <Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
            <Button sx={{marginRight: '1rem'}} variant="outlined" onClick={onClose}>Close</Button>
        </DialogActions>
        </Loadable>
        </Dialog>
    );
}
